import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Loader2, RefreshCw, Check, Edit3, Sparkles, Upload, Image, FolderOpen, Search, Clock, Plus, Folder, Star, X } from "lucide-react";
import { toast } from "sonner";
import { BASE_URL } from "../lib/api";
import { useAuthStore } from "../lib/store";

export default function CharacterPreviewModal({
  isOpen,
  onClose,
  onApprove,
  prompt,
  script,
  voiceSummary = "",
  style = "photorealistic"
}) {
  const [activeTab, setActiveTab] = useState("generate"); // "generate", "library", "upload"
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [characterData, setCharacterData] = useState(null);
  const [characterDescription, setCharacterDescription] = useState("");
  const [modifications, setModifications] = useState("");
  const [showEditMode, setShowEditMode] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const generationRef = useRef(false); // Prevent duplicate generations
  
  // Library state
  const [libraryImages, setLibraryImages] = useState([]);
  const [recentCharacters, setRecentCharacters] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [selectedLibraryImage, setSelectedLibraryImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateCollection, setShowCreateCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [isSavingToLibrary, setIsSavingToLibrary] = useState(false);
  
  // Upload state
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Clean up when modal closes
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
      if (uploadedImageUrl) {
        URL.revokeObjectURL(uploadedImageUrl);
      }
      setActiveTab("generate");
      setCharacterData(null);
      setCharacterDescription("");
      setModifications("");
      setShowEditMode(false);
      setImageUrl(null);
      setImageError(false);
      setSelectedLibraryImage(null);
      setUploadedImage(null);
      setUploadedImageUrl(null);
      setSearchQuery("");
      setSelectedCollection(null);
      setShowCreateCollection(false);
      setNewCollectionName("");
      generationRef.current = false;
    }
  }, [isOpen]);
  
  // Load library images when library tab is selected
  useEffect(() => {
    if (isOpen && activeTab === "library") {
      loadLibraryData();
    }
  }, [isOpen, activeTab]);
  
  // Load all library data including recent, collections, and images
  const loadLibraryData = async () => {
    setIsLoadingLibrary(true);
    try {
      const token = useAuthStore.getState().token || localStorage.getItem("auth_token");
      
      // Load recent characters, collections, and all character images in parallel
      const [recentRes, collectionsRes, imagesRes] = await Promise.all([
        fetch(`${BASE_URL}/api/characters/recent?limit=8`, {
          headers: { "Authorization": `Bearer ${token}` }
        }).catch(() => ({ ok: false })),
        fetch(`${BASE_URL}/api/characters/collections`, {
          headers: { "Authorization": `Bearer ${token}` }
        }).catch(() => ({ ok: false })),
        fetch(`${BASE_URL}/api/media?media_type=image&limit=100`, {
          headers: { "Authorization": `Bearer ${token}` }
        })
      ]);
      
      if (recentRes.ok) {
        const recentData = await recentRes.json();
        setRecentCharacters(recentData.characters || []);
      }
      
      if (collectionsRes.ok) {
        const collectionsData = await collectionsRes.json();
        setCollections(collectionsData.collections || []);
      }
      
      if (imagesRes.ok) {
        const imagesData = await imagesRes.json();
        setLibraryImages(imagesData.assets || []);
      }
    } catch (error) {
      console.error("Error loading library data:", error);
    } finally {
      setIsLoadingLibrary(false);
    }
  };
  
  // Save generated character to library
  const saveCharacterToLibrary = async (collectionId = null) => {
    if (!characterData) return;
    
    setIsSavingToLibrary(true);
    try {
      const token = useAuthStore.getState().token || localStorage.getItem("auth_token");
      const response = await fetch(`${BASE_URL}/api/characters/save-to-library`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          image_id: characterData.image_id,
          image_url: characterData.image_url,
          character_description: characterDescription,
          collection_id: collectionId,
          tags: ["character", "ai-generated"]
        })
      });
      
      if (response.ok) {
        toast.success("Character saved to library!");
        // Refresh library data
        loadLibraryData();
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      console.error("Error saving character:", error);
      toast.error("Failed to save character to library");
    } finally {
      setIsSavingToLibrary(false);
    }
  };
  
  // Create new collection
  const createCollection = async () => {
    if (!newCollectionName.trim()) return;
    
    try {
      const token = useAuthStore.getState().token || localStorage.getItem("auth_token");
      const response = await fetch(`${BASE_URL}/api/characters/collections`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newCollectionName.trim()
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCollections(prev => [...prev, data.collection]);
        setNewCollectionName("");
        setShowCreateCollection(false);
        toast.success("Collection created!");
      }
    } catch (error) {
      console.error("Error creating collection:", error);
      toast.error("Failed to create collection");
    }
  };
  
  // Track character usage for recent list
  const trackCharacterUsage = async (characterId) => {
    try {
      const token = useAuthStore.getState().token || localStorage.getItem("auth_token");
      await fetch(`${BASE_URL}/api/characters/track-usage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ character_id: characterId })
      });
    } catch (error) {
      // Silent fail - not critical
      console.debug("Failed to track character usage:", error);
    }
  };
  
  // Filter images by search query and collection
  const filteredImages = libraryImages.filter(image => {
    const matchesSearch = !searchQuery || 
      (image.title?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (image.description?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (image.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
    
    const matchesCollection = !selectedCollection || 
      image.collection_id === selectedCollection;
    
    return matchesSearch && matchesCollection;
  });
  
  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    event.target.value = ""; // allow re-selecting the same file after a failure
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }
    
    setIsUploading(true);
    // Local preview only; the approved reference must be the uploaded (server) URL
    const localUrl = URL.createObjectURL(file);
    setUploadedImageUrl(localUrl);
    setUploadedImage(null);
    
    try {
      const token = useAuthStore.getState().token || localStorage.getItem("auth_token");
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${BASE_URL}/api/upload/image`, {
        method: 'POST',
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) throw new Error(`Upload failed (${response.status})`);
      
      const data = await response.json();
      if (!data.image_url) throw new Error("Upload returned no image URL");
      setUploadedImage({
        id: data.filename,
        url: data.image_url.startsWith("http") ? data.image_url : `${BASE_URL}${data.image_url}`,
        localUrl,
      });
      toast.success("Image uploaded successfully!");
    } catch (error) {
      console.error("Error uploading image:", error);
      URL.revokeObjectURL(localUrl);
      setUploadedImageUrl(null);
      toast.error("Image upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };
  
  // Handle library image selection
  const handleLibrarySelect = (image) => {
    setSelectedLibraryImage(image);
  };
  
  // Handle approval based on active tab
  const handleApprove = async () => {
    if (activeTab === "generate" && characterData) {
      // Track usage for generated character
      if (characterData.image_id) {
        trackCharacterUsage(characterData.image_id);
      }
      onApprove({
        image_id: characterData.image_id,
        image_url: characterData.image_url,
        character_description: characterDescription,
        full_image_url: imageUrl,
        source: "generated"
      });
    } else if (activeTab === "library" && selectedLibraryImage) {
      // Track usage for library character
      trackCharacterUsage(selectedLibraryImage.id);
      onApprove({
        image_id: selectedLibraryImage.id,
        image_url: selectedLibraryImage.url,
        character_description: selectedLibraryImage.title || "Character from library",
        full_image_url: selectedLibraryImage.url,
        source: "library"
      });
    } else if (activeTab === "upload" && uploadedImage) {
      onApprove({
        image_id: uploadedImage.id || `upload-${Date.now()}`,
        image_url: uploadedImage.url,
        character_description: "Uploaded character reference",
        full_image_url: uploadedImage.url,
        source: "upload"
      });
    }
  };

  // Fetch image with retry logic
  const fetchImage = async (url, token, retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        const imgResponse = await fetch(`${BASE_URL}${url}`, {
          headers: { "Authorization": `Bearer ${token}` },
          cache: 'no-store' // Prevent caching issues
        });
        
        if (!imgResponse.ok) {
          throw new Error(`Image fetch failed: ${imgResponse.status}`);
        }
        
        const blob = await imgResponse.blob();
        if (blob.size === 0) {
          throw new Error("Empty image received");
        }
        
        return URL.createObjectURL(blob);
      } catch (error) {
        console.warn(`Image fetch attempt ${i + 1} failed:`, error);
        if (i === retries - 1) throw error;
        await new Promise(r => setTimeout(r, 1000)); // Wait 1s before retry
      }
    }
  };

  // Generate initial character preview
  const generatePreview = async () => {
    if (generationRef.current) return; // Prevent duplicate calls
    generationRef.current = true;
    
    setIsGenerating(true);
    setImageError(false);
    
    try {
      const token = useAuthStore.getState().token || localStorage.getItem("auth_token");
      const response = await fetch(`${BASE_URL}/api/characters/generate-preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt,
          script: script || "",
          style
        })
      });

      if (!response.ok) {
        throw new Error("Failed to generate character preview");
      }

      const data = await response.json();
      setCharacterData(data);
      setCharacterDescription(data.character_description);
      
      // Fetch the image with retry
      const newImageUrl = await fetchImage(data.image_url, token);
      setImageUrl(newImageUrl);
      
      toast.success("Character preview generated!");
    } catch (error) {
      console.error("Error generating character:", error);
      toast.error("Failed to generate character preview");
      setImageError(true);
      generationRef.current = false; // Allow retry
    } finally {
      setIsGenerating(false);
    }
  };

  // Regenerate with modifications
  const regenerateCharacter = async () => {
    setIsRegenerating(true);
    setImageError(false);
    
    // Store old URL for cleanup
    const oldImageUrl = imageUrl;
    
    try {
      const token = useAuthStore.getState().token || localStorage.getItem("auth_token");
      const response = await fetch(`${BASE_URL}/api/characters/regenerate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          character_description: characterDescription,
          modifications: modifications || "",
          style
        })
      });

      if (!response.ok) {
        throw new Error("Failed to regenerate character");
      }

      const data = await response.json();
      setCharacterData(data);
      setCharacterDescription(data.character_description);
      setModifications("");
      setShowEditMode(false);
      
      // Fetch the new image with retry
      const newImageUrl = await fetchImage(data.image_url, token);
      
      // Revoke old URL after new one is ready
      if (oldImageUrl) URL.revokeObjectURL(oldImageUrl);
      setImageUrl(newImageUrl);
      
      toast.success("Character regenerated!");
    } catch (error) {
      console.error("Error regenerating character:", error);
      toast.error("Failed to regenerate character");
      setImageError(true);
    } finally {
      setIsRegenerating(false);
    }
  };

  // Auto-generate on open if no character yet AND on generate tab
  useEffect(() => {
    if (isOpen && activeTab === "generate" && !characterData && !isGenerating && !generationRef.current) {
      generatePreview();
    }
  }, [isOpen, activeTab]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto bg-[#1A1A1B] border-border/50">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#00F0FF]" />
            Character Reference
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Choose how to provide a character reference for consistent visuals across your video.
          </DialogDescription>
        </DialogHeader>
        
        {/* Tab Navigation */}
        <div className="flex gap-1 p-1 bg-secondary/30 rounded-lg">
          <button
            onClick={() => setActiveTab("generate")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
              activeTab === "generate" 
                ? "bg-[#00F0FF] text-[#0A0A0B]" 
                : "text-muted-foreground hover:text-white hover:bg-secondary/50"
            }`}
            data-testid="tab-generate"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Generate New</span>
            <span className="sm:hidden">Generate</span>
          </button>
          <button
            onClick={() => setActiveTab("library")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
              activeTab === "library" 
                ? "bg-[#00F0FF] text-[#0A0A0B]" 
                : "text-muted-foreground hover:text-white hover:bg-secondary/50"
            }`}
            data-testid="tab-library"
          >
            <FolderOpen className="w-4 h-4" />
            <span className="hidden sm:inline">From Library</span>
            <span className="sm:hidden">Library</span>
          </button>
          <button
            onClick={() => setActiveTab("upload")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
              activeTab === "upload" 
                ? "bg-[#00F0FF] text-[#0A0A0B]" 
                : "text-muted-foreground hover:text-white hover:bg-secondary/50"
            }`}
            data-testid="tab-upload"
          >
            <Upload className="w-4 h-4" />
            <span>Upload</span>
          </button>
        </div>

        <div className="space-y-4 py-4 overflow-y-auto min-h-[300px]">
          {/* GENERATE TAB */}
          {activeTab === "generate" && (
            <>
              {/* Loading State */}
              {(isGenerating || (!characterData && !imageUrl && !imageError)) && (
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <Loader2 className="w-12 h-12 text-[#00F0FF] animate-spin" />
                  <p className="text-muted-foreground">Generating your character...</p>
                  <p className="text-xs text-muted-foreground">This may take 15-30 seconds</p>
                </div>
              )}

              {/* Error State */}
              {imageError && !isGenerating && (
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <p className="text-red-400">Failed to load character preview</p>
                  <Button
                    onClick={() => {
                      generationRef.current = false;
                      setImageError(false);
                      generatePreview();
                    }}
                    className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </Button>
                </div>
              )}

              {/* Character Preview */}
              {imageUrl && !isGenerating && !imageError && (
                <div className="space-y-4">
                  {/* Image Preview */}
                  <div className="relative rounded-lg overflow-hidden bg-secondary/30 border border-border/50">
                    <img
                      src={imageUrl}
                      alt="Character preview"
                      className="w-full h-auto max-h-[250px] sm:max-h-[350px] object-contain"
                      data-testid="character-preview-image"
                    />
                    {isRegenerating && (
                      <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
                      </div>
                    )}
                  </div>

                  {/* Character Description */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-white text-sm">Character Description</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowEditMode(!showEditMode)}
                        className="text-[#00F0FF] hover:text-[#00F0FF]/80"
                        data-testid="edit-description-btn"
                      >
                        <Edit3 className="w-4 h-4 mr-1" />
                        {showEditMode ? "Hide" : "Edit"}
                      </Button>
                    </div>
                    
                    {showEditMode ? (
                      <div className="space-y-3">
                        <Textarea
                          value={characterDescription}
                          onChange={(e) => setCharacterDescription(e.target.value)}
                          placeholder="Edit the character description..."
                          className="min-h-[80px] sm:min-h-[100px] bg-secondary/50 border-border/50 text-sm"
                          data-testid="character-description-input"
                        />
                        <div className="space-y-2">
                          <Label className="text-white text-sm">Quick Modifications</Label>
                          <Textarea
                            value={modifications}
                            onChange={(e) => setModifications(e.target.value)}
                            placeholder="e.g., 'add glasses', 'shorter hair', 'more serious expression', 'wearing a red shirt'"
                            className="min-h-[60px] bg-secondary/50 border-border/50 text-sm"
                            data-testid="modifications-input"
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground bg-secondary/30 p-3 rounded-lg">
                        {characterDescription}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* LIBRARY TAB */}
          {activeTab === "library" && (
            <div className="space-y-4">
              {isLoadingLibrary ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                  <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
                  <p className="text-muted-foreground">Loading your characters...</p>
                </div>
              ) : (
                <>
                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search characters..."
                      className="pl-10 bg-secondary/50 border-border/50"
                      data-testid="character-search-input"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Collections Filter */}
                  {collections.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedCollection(null)}
                        className={`px-3 py-1 text-xs rounded-full transition-all ${
                          !selectedCollection 
                            ? "bg-[#00F0FF] text-[#0A0A0B]" 
                            : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
                        }`}
                      >
                        All
                      </button>
                      {collections.map((collection) => (
                        <button
                          key={collection.id}
                          onClick={() => setSelectedCollection(collection.id)}
                          className={`px-3 py-1 text-xs rounded-full transition-all flex items-center gap-1 ${
                            selectedCollection === collection.id 
                              ? "bg-[#00F0FF] text-[#0A0A0B]" 
                              : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
                          }`}
                        >
                          <Folder className="w-3 h-3" />
                          {collection.name}
                        </button>
                      ))}
                      <button
                        onClick={() => setShowCreateCollection(true)}
                        className="px-3 py-1 text-xs rounded-full bg-secondary/30 text-muted-foreground hover:bg-secondary/50 flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        New
                      </button>
                    </div>
                  )}
                  
                  {/* Create Collection Modal */}
                  {showCreateCollection && (
                    <div className="p-3 bg-secondary/30 rounded-lg border border-border/50">
                      <Label className="text-white text-sm mb-2 block">New Collection Name</Label>
                      <div className="flex gap-2">
                        <Input
                          value={newCollectionName}
                          onChange={(e) => setNewCollectionName(e.target.value)}
                          placeholder="e.g., Business Presenters"
                          className="bg-secondary/50 border-border/50 flex-1"
                          onKeyDown={(e) => e.key === "Enter" && createCollection()}
                        />
                        <Button
                          onClick={createCollection}
                          size="sm"
                          className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"
                        >
                          Create
                        </Button>
                        <Button
                          onClick={() => {
                            setShowCreateCollection(false);
                            setNewCollectionName("");
                          }}
                          size="sm"
                          variant="ghost"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Recent Characters Section */}
                  {recentCharacters.length > 0 && !searchQuery && !selectedCollection && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>Recently Used</span>
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {recentCharacters.slice(0, 6).map((character) => (
                          <button
                            key={character.id}
                            onClick={() => handleLibrarySelect(character)}
                            className={`relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                              selectedLibraryImage?.id === character.id
                                ? "border-[#00F0FF] ring-2 ring-[#00F0FF]/30"
                                : "border-transparent hover:border-border"
                            }`}
                          >
                            <img
                              src={character.url || character.thumbnail_url}
                              alt={character.title || "Recent character"}
                              className="w-full h-full object-cover"
                            />
                            {selectedLibraryImage?.id === character.id && (
                              <div className="absolute inset-0 bg-[#00F0FF]/20 flex items-center justify-center">
                                <Check className="w-4 h-4 text-[#00F0FF]" />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Main Image Grid */}
                  {filteredImages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 space-y-4">
                      <Image className="w-12 h-12 text-muted-foreground" />
                      <p className="text-muted-foreground">
                        {searchQuery ? "No characters match your search" : "No character images in your library yet"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {searchQuery ? "Try a different search term" : "Generate characters and save them to reuse later"}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="text-xs text-muted-foreground">
                        {filteredImages.length} character{filteredImages.length !== 1 ? 's' : ''} {searchQuery && 'found'}
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[280px] overflow-y-auto p-1">
                        {filteredImages.map((image) => (
                          <button
                            key={image.id}
                            onClick={() => handleLibrarySelect(image)}
                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all group ${
                              selectedLibraryImage?.id === image.id
                                ? "border-[#00F0FF] ring-2 ring-[#00F0FF]/30"
                                : "border-transparent hover:border-border"
                            }`}
                            data-testid={`library-image-${image.id}`}
                          >
                            <img
                              src={image.url || image.thumbnail_url}
                              alt={image.title || "Library image"}
                              className="w-full h-full object-cover"
                            />
                            {selectedLibraryImage?.id === image.id && (
                              <div className="absolute inset-0 bg-[#00F0FF]/20 flex items-center justify-center">
                                <Check className="w-6 h-6 text-[#00F0FF]" />
                              </div>
                            )}
                            {/* Hover overlay with title */}
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-[10px] text-white truncate">{image.title || "Character"}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  
                  {/* Selected Image Info */}
                  {selectedLibraryImage && (
                    <div className="p-3 bg-secondary/30 rounded-lg flex items-center gap-3">
                      <img 
                        src={selectedLibraryImage.url || selectedLibraryImage.thumbnail_url} 
                        alt="" 
                        className="w-12 h-12 rounded object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{selectedLibraryImage.title || "Selected character"}</p>
                        <p className="text-xs text-muted-foreground">Ready to use as character reference</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* UPLOAD TAB */}
          {activeTab === "upload" && (
            <div className="space-y-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="upload-input"
              />
              
              {!uploadedImageUrl ? (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center py-12 space-y-4 border-2 border-dashed border-border/50 rounded-lg cursor-pointer hover:border-[#00F0FF]/50 hover:bg-secondary/20 transition-all"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-12 h-12 text-[#00F0FF] animate-spin" />
                      <p className="text-muted-foreground">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-muted-foreground" />
                      <p className="text-muted-foreground">Click to upload an image</p>
                      <p className="text-xs text-muted-foreground">PNG, JPG, WEBP up to 10MB</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative rounded-lg overflow-hidden bg-secondary/30 border border-border/50">
                    <img
                      src={uploadedImageUrl}
                      alt="Uploaded character"
                      className="w-full h-auto max-h-[350px] object-contain"
                      data-testid="uploaded-preview-image"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (uploadedImageUrl) URL.revokeObjectURL(uploadedImageUrl);
                        setUploadedImage(null);
                        setUploadedImageUrl(null);
                      }}
                      className="border-border/50"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Choose Different Image
                    </Button>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-sm text-white">Image uploaded successfully</p>
                    <p className="text-xs text-muted-foreground mt-1">This image will be used as your character reference</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {voiceSummary && (
          <p className="text-sm text-white px-3 py-2 rounded-lg bg-secondary/40 border border-border/40" data-testid="character-modal-voice-summary">
            <span className="text-muted-foreground">Voice: </span>{voiceSummary}
          </p>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-4">
          {/* Generate Tab Actions */}
          {activeTab === "generate" && imageUrl && !isGenerating && !imageError && (
            <>
              <Button
                variant="outline"
                onClick={() => saveCharacterToLibrary()}
                disabled={isRegenerating || isSavingToLibrary}
                className="border-border/50 w-full sm:w-auto"
                data-testid="save-to-library-btn"
              >
                {isSavingToLibrary ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FolderOpen className="w-4 h-4 mr-2" />
                )}
                {isSavingToLibrary ? "Saving..." : "Save to Library"}
              </Button>
              <Button
                variant="outline"
                onClick={regenerateCharacter}
                disabled={isRegenerating || isSavingToLibrary}
                className="border-border/50 w-full sm:w-auto"
                data-testid="regenerate-character-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRegenerating ? 'animate-spin' : ''}`} />
                {isRegenerating ? "Regenerating..." : "Regenerate"}
              </Button>
              <Button
                onClick={handleApprove}
                disabled={isRegenerating || isSavingToLibrary}
                className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] w-full sm:w-auto"
                data-testid="approve-character-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Use This Character
              </Button>
            </>
          )}
          
          {activeTab === "generate" && !imageUrl && !isGenerating && !imageError && (
            <Button
              onClick={generatePreview}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] w-full sm:w-auto"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Character
            </Button>
          )}
          
          {/* Library Tab Actions */}
          {activeTab === "library" && (
            <Button
              onClick={handleApprove}
              disabled={!selectedLibraryImage}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] w-full sm:w-auto disabled:opacity-50"
              data-testid="use-library-image-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Use Selected Image
            </Button>
          )}
          
          {/* Upload Tab Actions */}
          {activeTab === "upload" && (
            <Button
              onClick={handleApprove}
              disabled={!uploadedImage?.url || isUploading}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] w-full sm:w-auto disabled:bg-white/10 disabled:text-white/40 disabled:opacity-100"
              data-testid="use-uploaded-image-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Use Uploaded Image
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
